const { getValidatedIframeData, isObjectEmpty } = require('../util');
const { method } = require('../variables');
const {
  CSS_CLASSES,
  EVENT_TYPES,
  HEIGHT_ADJUSTMENTS,
  HTTP_STATUS,
  IFRAME_TYPES,
  SCRIPT_PROPS,
  STATUS,
  PAYMENT_TYPES,
} = require('../constants');
const { mainConfigService } = require('../services/config');
const {
  extractCardDataFromEvent,
  extractCardDataFromMethod,
} = require('../utils/cardDataExtractor');

class JazzIntegration {
  constructor({
    selector,
    fieldsService,
    callBackService,
    sessionId,
    merchantId,
    apiKey,
    data,
    paymentType,
    lang,
  }) {
    this.selector = selector;
    this.fieldsService = fieldsService;
    this.callBackService = callBackService;
    this.documentCheckout = selector.documentCheckout;
    this.sessionId = sessionId;
    this.merchantId = merchantId;
    this.apiKey = apiKey;
    this.data = data;
    this.paymentType = paymentType;
    this.lang = lang;

    this.SDKEventBus = new EventTarget();
    this.jazzSdk = null;
    this.iframeManagers = new Map(); // Store iframe managers by their IDs
    this.isInitialized = false;
  }

  async loadSDKScript(containerElement) {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = process.env.JAZZ_SDK_URL;
      script.type = SCRIPT_PROPS.TYPE_JAVASCRIPT;

      // Add SRI (Subresource Integrity) attributes if hash is provided and not running locally
      if (
        process.env.JAZZ_SDK_INTEGRITY_HASH &&
        process.env.IS_LOCAL !== 'true'
      ) {
        script.integrity = process.env.JAZZ_SDK_INTEGRITY_HASH;
        script.crossOrigin = 'anonymous';
      }

      script.onload = () => {
        console.log('[JazzSDK] Jazz SDK loaded successfully');
        resolve();
      };
      script.onerror = () => {
        const errorMessage = process.env.JAZZ_SDK_INTEGRITY_HASH
          ? 'Failed to load JazzSDK script. This could be due to a security integrity check failure or network issue.'
          : 'Failed to load JazzSDK script';
        console.error('[JazzSDK] Failed to load Jazz SDK:', errorMessage);
        reject(new Error(errorMessage));
      };
      containerElement.appendChild(script);
    });
  }

  async init() {
    if (this.isInitialized) {
      return; // Already initialized
    }

    // Load SDK script into the document head instead of a specific container
    await this.loadSDKScript(document.head);

    // Initialize JazzSDK once
    this.jazzSdk = new window.JazzSDK({
      apiKey: this.apiKey,
      merchantId: this.merchantId,
      eventTarget: this.SDKEventBus,
      lang: this.lang,
    });

    this.setupEventListeners();
    this.setupIframeReadyListener();
    this.isInitialized = true;
  }

  /**
   * Returns the merchant theme to forward to the Jazz iframe, or a fallback
   * that makes the iframe's card-form border transparent so it doesn't draw
   * a second border on top of the SDK-level card outline.
   */
  getTheme() {
    const theme = mainConfigService.get('theme');
    if (theme && !isObjectEmpty(theme)) {
      return theme;
    }
    return {
      'card-background': {
        'border-color': 'transparent',
      },
    };
  }

  /**
   * Creates a card iframe with the given configuration
   * @param {Object} config - Configuration for the card iframe
   * @param {string} config.containerId - ID of the container element
   * @param {string} config.iframeName - Name for the iframe
   * @param {Object} config.postData - Data to post to the iframe
   * @returns {Object} - Object containing iframeId and iframeManager
   */
  createCardIframe({ containerId, iframeName, postData }) {
    if (!this.isInitialized) {
      throw new Error(
        'JazzIntegration must be initialized before creating iframes'
      );
    }
    const container = this.documentCheckout.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with ID "${containerId}" not found`);
    }

    const validatedData = getValidatedIframeData(postData || this.data);
    const directPayConfig = {
      ...validatedData,
      theme: this.getTheme(),
      session_id: this.sessionId,
      payment_type: this.paymentType,
    };

    const iframeConfig = {
      container,
      iframeName,
      postData: directPayConfig,
    };

    const result = this.jazzSdk.createCardIframe(iframeConfig);
    this.iframeManagers.set(result.iframeId, {
      type: IFRAME_TYPES.CARD,
      containerId,
      iframeName,
      manager: result.iframeManager,
    });

    const iframe = this.documentCheckout.getElementById(result.iframeId);
    if (iframe) {
      iframe.classList.add(CSS_CLASSES.DISPLAY_NONE);
    }

    return result;
  }

  /**
   * Creates a CVV iframe with the given configuration
   * @param {Object} config - Configuration for the CVV iframe
   * @param {string} config.containerId - ID of the container element
   * @param {string} config.iframeName - Name for the iframe
   * @param {Object} config.postData - Data to post to the iframe
   * @returns {Object} - Object containing iframeId and iframeManager
   */
  createCvvIframe({ containerId, iframeName, postData }) {
    if (!this.isInitialized) {
      throw new Error(
        'JazzIntegration must be initialized before creating iframes'
      );
    }

    const container = this.documentCheckout.getElementById(containerId);
    if (!container) {
      throw new Error(`Container with ID "${containerId}" not found`);
    }

    const matchedMethod = mainConfigService
      .get('payment_methods')
      ?.find((pm) => pm.code === postData.pg_code);

    const validatedData = getValidatedIframeData({
      ...matchedMethod,
      ...postData,
    });

    const directPayConfig = {
      ...validatedData,
      theme: this.getTheme(),
      session_id: this.sessionId,
      payment_type: this.paymentType,
    };

    const iframeConfig = {
      container,
      iframeName,
      postData: directPayConfig,
    };

    const result = this.jazzSdk.createCvvIframe(iframeConfig);
    this.iframeManagers.set(result.iframeId, {
      type: IFRAME_TYPES.CVV,
      containerId,
      iframeName,
      manager: result.iframeManager,
    });

    const iframe = this.documentCheckout.getElementById(result.iframeId);
    if (iframe) {
      iframe.classList.add(CSS_CLASSES.DISPLAY_NONE);
    }

    return result;
  }

  setupIframeReadyListener() {
    const self = this;
    window.addEventListener('message', (event) => {
      const { type, iframeId, containerId } = event.data || {};
      // Check if we have this iframe registered
      if (!self.iframeManagers.has(iframeId)) {
        return;
      }

      if (
        type === EVENT_TYPES.CVV_IFRAME_READY ||
        type === EVENT_TYPES.IFRAME_READY
      ) {
        const loader = self.documentCheckout.getElementById(
          `loader-${containerId}`
        );
        const iframe = self.documentCheckout.getElementById(iframeId);

        if (iframe) {
          // Handle CVV iframe separately from Card iframe
          if (type === EVENT_TYPES.CVV_IFRAME_READY) {
            iframe.classList.add(CSS_CLASSES.CVV_STYLES);

            // CVV iframe fade-in with saved-card-loader fade-out
            const cardLoaders =
              self.documentCheckout.querySelectorAll('.saved-card-loader');

            cardLoaders.forEach((cardLoader) => {
              const cardKey = cardLoader.getAttribute('data-card-key');
              if (containerId.includes(cardKey)) {
                // Prepare CVV iframe for fade-in
                iframe.classList.remove(CSS_CLASSES.DISPLAY_NONE);
                iframe.classList.add(CSS_CLASSES.IFRAME_FADE);

                // Force reflow
                iframe.offsetHeight;

                // Start cross-fade animation
                requestAnimationFrame(() => {
                  // Fade in CVV iframe
                  iframe.classList.add(CSS_CLASSES.IFRAME_VISIBLE);

                  // Fade out saved-card-loader simultaneously
                  cardLoader.classList.add(
                    CSS_CLASSES.SAVED_CARD_LOADER_FADE_OUT
                  );

                  // Remove loader from DOM after transition completes
                  setTimeout(() => {
                    cardLoader.classList.add(
                      CSS_CLASSES.SAVED_CARD_LOADER_LOADED
                    );
                  }, 300);
                });
              }
            });
          } else if (type === EVENT_TYPES.IFRAME_READY) {
            // Handle Card iframe with loader cross-fade
            // Step 1: Set up parent container with proper positioning and height
            const parentContainer = iframe.parentElement;
            if (parentContainer && loader) {
              const loaderHeight = loader.offsetHeight;
              // Reserve space in layout by setting height on parent
              parentContainer.style.height = `${loaderHeight}px`;
              parentContainer.classList.add(
                CSS_CLASSES.IFRAME_TRANSITION_CONTAINER
              );
            }

            // Step 2: Position iframe absolutely (like the loader) to prevent layout shift
            iframe.classList.add(CSS_CLASSES.IFRAME_TRANSITION_ABSOLUTE);

            // Step 3: Prepare iframe for fade-in (but keep it invisible)
            iframe.classList.remove(CSS_CLASSES.DISPLAY_NONE);
            iframe.classList.add(CSS_CLASSES.IFRAME_FADE);

            // Force reflow to ensure initial state is applied
            iframe.offsetHeight;

            // Step 4: Start cross-fade animation
            requestAnimationFrame(() => {
              // Fade in iframe
              iframe.classList.add(CSS_CLASSES.IFRAME_VISIBLE);

              // Fade out loader simultaneously
              if (loader) {
                loader.classList.add(CSS_CLASSES.LOADER_FADE_OUT);
              }

              // Step 5: Clean up after transition completes
              setTimeout(() => {
                if (loader) {
                  loader.classList.add(CSS_CLASSES.DISPLAY_NONE);
                }

                // Return iframe to normal flow and let it size naturally
                iframe.classList.remove(CSS_CLASSES.IFRAME_TRANSITION_ABSOLUTE);

                // Let parent container size naturally
                if (parentContainer) {
                  parentContainer.style.height = '';
                  parentContainer.classList.remove(
                    CSS_CLASSES.IFRAME_TRANSITION_CONTAINER
                  );
                }
              }, 300); // Match the transition duration
            });
          }
        }
      }
    });
  }

  setupEventListeners() {
    this._setupCardValidationListener();
    this._setupCvvValidationListener();
    this._setupHandlePaymentProcessListener();
    this._setupCardEncryptionTriggerListener();
    this._setupEncryptionFailureListener();
    this._setupOnsiteFormHeightChangeListener();
  }

  _setupCardValidationListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.VALID_CARD_FIELD,
      async (event) => {
        try {
          const iframeId = event.detail?.iframeId;

          // Check if we have this iframe registered
          if (!this.iframeManagers.has(iframeId)) {
            return;
          }

          this.fieldsService.disableEnableFields(true);

          // Extract card data and invoke callback
          const card = extractCardDataFromEvent(event.detail);
          await this._invokeBeforePaymentCallback(card);

          const paymentType = mainConfigService.get('payment_type');
          const paymentMethod = mainConfigService
            .get('payment_methods')
            ?.find((m) => m.code === method.code);

          if (paymentType === PAYMENT_TYPES.SAVE_CARD) {
            method.save_card = true;
            method.direct_payment_url =
              paymentMethod.direct_save_card_url || method.direct_payment_url;
          } else {
            const saveCardCheckbox = this.documentCheckout.querySelector(
              '#ottu__sdk-card-save'
            );
            method.save_card = saveCardCheckbox
              ? saveCardCheckbox.checked
              : false;
          }

          this.jazzSdk.submitCardDetails(iframeId, method);
        } catch (err) {
          this._handleJazzEventError(err);
        }
      }
    );
  }

  _setupCvvValidationListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.VALID_CVV_FIELD,
      async (event) => {
        try {
          const iframeId = event.detail?.iframeId;

          // Check if we have this iframe registered
          if (!this.iframeManagers.has(iframeId)) {
            return;
          }

          const configData = {
            merchant_id: this.merchantId,
            api_key: this.apiKey,
          };
          const matchedMethod = mainConfigService
            .get('payment_methods')
            ?.find((pm) => pm.code === method.card_pg);
          configData.encKeyUrl = matchedMethod.enc_key_url;
          configData.trandata = matchedMethod.trandata;
          configData.submit_url = method.submit_url;

          this.fieldsService.disableEnableFields(true);

          // Extract card data from method and invoke callback
          const card = extractCardDataFromMethod(method);
          await this._invokeBeforePaymentCallback(card);

          this.jazzSdk.submitCvv(iframeId, method, configData);
        } catch (err) {
          this._handleJazzEventError(err);
        }
      }
    );
  }

  _setupHandlePaymentProcessListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.HANDLE_PAYMENT_PROCESS,
      (event) => {
        const data = event.detail;
        const iframeId = data?.iframeId;
        // Check if we have this iframe registered
        if (!this.iframeManagers.has(iframeId)) {
          return;
        }

        this.fieldsService.disableEnableFields(false);
        if (data.status?.toLowerCase() === STATUS.ERROR) {
          const payButton = this.selector._checkPayButtonExists();
          payButton.classList.remove(CSS_CLASSES.DISABLED_PAY_BUTTON);
          this.selector.errorResponse(data);
          this.jazzSdk.handle3DSApiError(iframeId);
          this.jazzSdk.handleFieldErrors(iframeId, {});
        } else if (data.status === HTTP_STATUS.INTERNAL_SERVER_ERROR) {
          this.selector.callBackError(data);
          this.jazzSdk.handleFieldErrors(iframeId, {});
        } else if (data.httpStatus === HTTP_STATUS.BAD_REQUEST) {
          this.jazzSdk.handleFieldErrors(iframeId, data);
          this.fieldsService.disableEnableFields(false);
          const payButton = this.selector._checkPayButtonExists();
          payButton.classList.remove(CSS_CLASSES.DISABLED_PAY_BUTTON);
        } else {
          if (
            data.status === STATUS.CANCELED ||
            data.status === STATUS.FAILED
          ) {
            const payButton = this.selector._checkPayButtonExists();
            payButton.classList.remove(CSS_CLASSES.DISABLED_PAY_BUTTON);
          }
          this.selector.successResponse(data);
          this.jazzSdk.handleFieldErrors(iframeId, {});
        }
      }
    );
  }

  _setupOnsiteFormHeightChangeListener() {
    this.SDKEventBus.addEventListener(EVENT_TYPES.CHANGE_HEIGHT, (event) => {
      const data = event.detail;
      const iframeId = data?.iframeId;

      // Check if we have this iframe registered
      if (!this.iframeManagers.has(iframeId)) {
        return;
      }

      const iframeDiv = this.selector._changeOnsiteIframeHeight();
      const newHeight = data.height + HEIGHT_ADJUSTMENTS.ONSITE_FORM_PADDING;
      iframeDiv.style.height = `${newHeight}px`;
    });
  }

  _setupCardEncryptionTriggerListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.CARD_ENCRYPTION_STARTED,
      (event) => {
        const iframeId = event.detail?.iframeId;

        // Check if we have this iframe registered
        if (!this.iframeManagers.has(iframeId)) {
          return;
        }

        const payButton = this.selector._checkPayButtonExists();
        payButton.classList.add(CSS_CLASSES.DISABLED_PAY_BUTTON);
      }
    );
  }

  _setupEncryptionFailureListener() {
    this.SDKEventBus.addEventListener(
      EVENT_TYPES.ENCRYPTION_FAILURE,
      (event) => {
        const data = event.detail;
        const iframeId = data?.iframeId;

        // Check if we have this iframe registered
        if (!this.iframeManagers.has(iframeId)) {
          return;
        }

        if (data.error) {
          this.selector.errorResponse(data.error);
          this.fieldsService.disableEnableFields(false);
          const payButton = this.selector._checkPayButtonExists();
          payButton.classList.remove(CSS_CLASSES.DISABLED_PAY_BUTTON);
        }
      }
    );
  }

  /**
   * Invokes beforePayment callback with card data
   * Handles both cases: with card data and without (backward compatible)
   * @private
   * @param {Object} card - Card data object
   * @returns {Promise<void>}
   */
  async _invokeBeforePaymentCallback(card) {
    const hasData = !!(card.number || card.brand);

    if (hasData) {
      await this.callBackService.callBackPayment({ card });
    } else {
      await this.callBackService.callBackPayment();
    }
  }

  /**
   * Handles errors from Jazz SDK event listeners
   * Standardizes error handling across all Jazz integration event handlers
   * @private
   * @param {Error} error - The error that occurred
   */
  _handleJazzEventError(error) {
    this.fieldsService.disableEnableFields(false);
    console.error(error);
    this.selector.errorResponse(error);
  }

  /**
   * Validates card fields for a specific iframe
   * @param {string} iframeId - ID of the iframe to validate
   */
  validateCardFields(iframeId) {
    if (!this.iframeManagers.has(iframeId)) {
      throw new Error(`Iframe with ID "${iframeId}" not found`);
    }
    this.jazzSdk?.validateCardFields(iframeId);
  }

  /**
   * Validates CVV field for a specific iframe
   * @param {string} iframeId - ID of the iframe to validate
   */
  validateCvvField(iframeId) {
    if (!this.iframeManagers.has(iframeId)) {
      throw new Error(`Iframe with ID "${iframeId}" not found`);
    }
    this.jazzSdk?.validateCvvField(iframeId);
  }

  /**
   * Removes CSS classes for a specific iframe
   * @param {string} iframeId - ID of the iframe
   */
  unselectOnsiteMethod(iframeId) {
    if (!this.iframeManagers.has(iframeId)) {
      throw new Error(`Iframe with ID "${iframeId}" not found`);
    }
    this.jazzSdk?.removeCSSClasses(iframeId);
  }

  /**
   * Gets all iframe IDs managed by this instance
   * @returns {Array} - Array of iframe IDs
   */
  getAllIframeIds() {
    return Array.from(this.iframeManagers.keys());
  }

  /**
   * Gets iframe manager information
   * @param {string} iframeId - ID of the iframe
   * @returns {Object} - Iframe manager information
   */
  getIframeManager(iframeId) {
    return this.iframeManagers.get(iframeId);
  }

  /**
   * Destroys a specific iframe
   * @param {string} iframeId - ID of the iframe to destroy
   */
  destroyIframe(iframeId) {
    if (!this.iframeManagers.has(iframeId)) {
      throw new Error(`Iframe with ID "${iframeId}" not found`);
    }

    this.jazzSdk?.destroyIframe(iframeId);
    this.iframeManagers.delete(iframeId);
  }

  /**
   * Destroys all iframes and cleans up the instance
   */
  destroy() {
    // Destroy all iframes
    this.getAllIframeIds().forEach((iframeId) => {
      this.jazzSdk?.destroyIframe(iframeId);
    });

    this.iframeManagers.clear();
    this.jazzSdk = null;
    this.isInitialized = false;
  }
}

exports.JazzIntegration = JazzIntegration;
